import { useEffect, useRef, useState } from "react";

import { WORLD_SESSION_TIMES_MAX } from "@cleo/contracts";

import { sessionsToAsk, type SessionTimesRound } from "./roomRailQuiet";
import { readWorldSessionTimes } from "./worldSessionClient";

// Can 80 (the principal, 17 Oct 2026: "the quantity of sessions is
// overwhelming"): when each session was last active in its World, from any
// device, so the rail can fold quiet sessions this Desk never talked to.
// Each session is asked once per round (one World, REFRESH_MS); each answer
// is merged as it arrives and a read is never abandoned because the index
// changed. A failed read waits for the next round. View only. A session the
// World says is retired (archived on another device, or by a tidy) is told
// to onRetired: its retirement is its latest step, so by time alone it would
// stay in view as open until the World is read again.
const REFRESH_MS = 5 * 60_000;
const EMPTY: ReadonlyMap<string, number> = new Map();

export function useWorldSessionTimes(
  worldKey: string | null | undefined,
  sessionIds: ReadonlyArray<string>,
  enabled: boolean,
  now: number,
  onRetired?: (sessionIds: ReadonlyArray<string>) => void,
): ReadonlyMap<string, number> {
  const [held, setHeld] = useState<{
    readonly worldKey: string;
    readonly times: ReadonlyMap<string, number>;
  } | null>(null);
  const round = useRef<SessionTimesRound | null>(null);
  const retiredRef = useRef(onRetired);
  useEffect(() => {
    retiredRef.current = onRetired;
  }, [onRetired]);
  const slot = Math.floor(now / REFRESH_MS);
  useEffect(() => {
    if (!enabled || typeof worldKey !== "string") return;
    const next = sessionsToAsk(round.current, `${worldKey}\n${slot}`, sessionIds);
    round.current = next.round;
    for (let at = 0; at < next.ask.length; at += WORLD_SESSION_TIMES_MAX) {
      const chunk = next.ask.slice(at, at + WORLD_SESSION_TIMES_MAX);
      void readWorldSessionTimes(worldKey, chunk).then((times) => {
        if (times === undefined || times.size === 0) return;
        setHeld((previous) => {
          const merged = new Map(previous?.worldKey === worldKey ? previous.times : EMPTY);
          for (const [id, time] of times) merged.set(id, time.activeAt);
          return { worldKey, times: merged };
        });
        const retired = [...times].filter(([, time]) => time.retired).map(([id]) => id);
        if (retired.length > 0) retiredRef.current?.(retired);
      });
    }
  }, [enabled, worldKey, slot, sessionIds]);
  return held !== null && held.worldKey === worldKey ? held.times : EMPTY;
}
